import { AlertTriangle } from 'lucide-react';

import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { RouteScheduleForecastInterface } from '@/types';
import { tv } from '@/utils/styles';

import { SchedulesForecastCard } from '../forecast-card';

export interface ScheduleForecastIndicatorProps {
  forecast?: RouteScheduleForecastInterface;
  timezone?: string;
  isEnabled?: boolean;
  children: React.ReactNode;
  isArrival?: boolean;
}

const ScheduleForecastIndicator = ({
  timezone,
  forecast,
  isEnabled = true,
  children,
  isArrival = false,
}: ScheduleForecastIndicatorProps) => {
  if (!forecast || !isEnabled) return <div>{children}</div>;

  const forecastDateTime = isArrival ? forecast.forecastArrivalDateTime : forecast.forecastDepartureDateTime;
  const scheduledDateTime = isArrival ? forecast.scheduledArrivalDateTime : forecast.scheduledDepartureDateTime;
  const delta = isArrival ? forecast.arrivalDelta : forecast.departureDelta;
  const hasDelta = !!(delta && delta !== 0);
  const isDelayed = !!(delta && delta > 0);
  const isEarly = !!(delta && delta < 0);

  const { base, content, bg, forecastBadge } = styles({
    isForecast: hasDelta,
  });

  return (
    <div className={base()}>
      <span className={bg()} />
      <HoverCard openDelay={150}>
        <HoverCardTrigger>
          <div className={content()}>
            <div>{children}</div>
            {hasDelta && (
              <div className={forecastBadge()}>
                <AlertTriangle size={14} className="fill-red-50 stroke-red-700" />
              </div>
            )}
          </div>
        </HoverCardTrigger>
        <HoverCardContent align="start" alignOffset={-18}>
          <SchedulesForecastCard
            forecastDate={forecastDateTime!}
            scheduledDate={scheduledDateTime}
            timezone={timezone}
            hasForecast={hasDelta}
            isEarly={isEarly}
            isDelayed={isDelayed}
            isArrival={isArrival}
          />
        </HoverCardContent>
      </HoverCard>
    </div>
  );
};

const styles = tv({
  slots: {
    base: 'group/indicator relative w-full',
    content: 'relative z-2 flex items-center pr-9 leading-[14px]',
    bg: 'absolute -bottom-1.5 -left-2.5 -top-1.5 right-1.5 z-1 rounded-md group-hover/indicator:bg-grey-50',
    forecastBadge: 'absolute -top-0.5 right-2.5 z-1 grid size-5 place-items-center',
  },
  variants: {
    isForecast: {
      true: {
        bg: 'bg-[#F6FBFF] group-hover/indicator:!bg-[#EEF7FE]',
      },
      false: null,
    },
  },
});

export { ScheduleForecastIndicator };
